// React auth state over the Cognito wrapper. When Cognito isn't configured
// (local dev without a pool), status is "disabled" and the app runs open with
// sample data — the same graceful-fallback pattern as config/data loading.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  completeNewPassword as cognitoCompleteNewPassword,
  getClaims,
  isAuthConfigured,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  type IdTokenClaims,
} from "./cognito";
import { clearViewAs, getViewAs, type ViewAsTarget } from "./viewAsStore";

export type AuthStatus =
  | "disabled" // no pool configured — run open (local dev)
  | "loading"
  | "signedOut"
  | "newPasswordRequired" // invited user must choose a real password
  | "signedIn";

export interface AuthState {
  status: AuthStatus;
  /** Effective claims the app renders from — the view-as target's tenancy
   *  while support impersonation is active, otherwise the signed-in user's. */
  claims: IdTokenClaims | null;
  /** Active support view-as target (internal staff only), or null. */
  viewAs: ViewAsTarget | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const configured = isAuthConfigured();
  const [status, setStatus] = useState<AuthStatus>(configured ? "loading" : "disabled");
  const [claims, setClaims] = useState<IdTokenClaims | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const c = await getClaims();
    setClaims(c);
    setStatus(c ? "signedIn" : "signedOut");
  }, []);

  useEffect(() => {
    if (configured) void refresh();
  }, [configured, refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const result = await cognitoSignIn(email, password);
        if (result.kind === "new_password_required") {
          setStatus("newPasswordRequired");
        } else {
          await refresh();
        }
      } catch (e) {
        setError((e as Error).message);
        throw e;
      }
    },
    [refresh],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      setError(null);
      try {
        await cognitoCompleteNewPassword(newPassword);
        await refresh();
      } catch (e) {
        setError((e as Error).message);
        throw e;
      }
    },
    [refresh],
  );

  const signOut = useCallback(() => {
    clearViewAs(); // never carry a support view across accounts
    cognitoSignOut();
    setClaims(null);
    setStatus("signedOut");
  }, []);

  // Support view-as (sessionStorage, set via lib/auth/viewAs.ts which reloads
  // the page): overlay the target's tenancy attributes so every claims consumer
  // (hub app list, pinned client, reports) renders exactly the target's view.
  // The server independently re-validates each data request.
  const viewAs = getViewAs();
  const effectiveClaims: IdTokenClaims | null =
    viewAs && claims
      ? {
          ...claims,
          email: viewAs.email,
          name: viewAs.name,
          "custom:project_string": viewAs.project_string,
          "custom:client": viewAs.client ?? "",
          "custom:role": viewAs.role ?? "",
          "custom:billing": viewAs.billing ?? "",
          "custom:default_app": viewAs.default_app ?? "",
        }
      : claims;

  return (
    <AuthContext.Provider
      value={{ status, claims: effectiveClaims, viewAs, error, signIn, completeNewPassword, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
